/**
 * Query Submission Service for Hilton Analytica COA Platform
 * 
 * Handles submission of user queries about COA requests to the backend Lambda.
 * Provides retry logic, timeout handling, and error management.
 */

import {
  SubmissionResponse,
  LambdaErrorResponse,
  RequestOptions,
} from '../types';
import { BaseApiService, ApiServiceError } from './baseApiService';

/**
 * Input data for query submission
 * requestId or productName is required (at least one must be provided)
 * Omits auto-generated fields (id, submittedAt, status) that are set by the backend
 */
export interface QuerySubmissionData {
  requestId?: string;
  productName?: string;
  email: string;
  queryType: string;
  message: string;
}

/**
 * Configuration for the submit-query Lambda endpoint
 */
const SUBMIT_QUERY_ENDPOINT = '/submit-query';
const SUBMIT_QUERY_TIMEOUT = 10000; // 10 seconds as per requirement 5.2

/**
 * Default options for query submissions
 */
const DEFAULT_QUERY_OPTIONS: RequestOptions = {
  timeout: SUBMIT_QUERY_TIMEOUT,
  retries: 3,
  retryDelay: 1000,
};

/**
 * Query Submission Service class for submitting queries
 */
export class QueryService {
  private apiService: BaseApiService;

  constructor(apiService?: BaseApiService) {
    // Initialize with default configuration
    this.apiService = apiService || new BaseApiService('', DEFAULT_QUERY_OPTIONS);
  }

  /**
   * Submit a query to the Lambda endpoint
   * 
   * @param queryData - Query data including requestId or productName, email, queryType, and message
   * @returns Promise resolving to SubmissionResponse with requestId, message, and submittedAt
   * @throws ApiServiceError if the request fails after all retry attempts or receives 4xx/5xx error
   * 
   * Features:
   * - 10 second timeout (requirement 5.2)
   * - Retry logic up to 3 attempts (handled by BaseApiService)
   * - Handles 2xx success responses
   * - Handles 4xx/5xx error responses
   */
  async submitQuery(queryData: QuerySubmissionData): Promise<SubmissionResponse> {
    try {
      // Make POST request to submit-query Lambda endpoint
      const response = await this.apiService.post<SubmissionResponse>(
        SUBMIT_QUERY_ENDPOINT,
        queryData,
        DEFAULT_QUERY_OPTIONS
      );

      // Handle successful 2xx response
      if (response.success) {
        return response.data;
      }

      // Handle error response from Lambda
      const errorResponse = response as LambdaErrorResponse;
      throw new ApiServiceError(
        errorResponse.error || 'Failed to submit query',
        500,
        errorResponse.code
      );
    } catch (error) {
      if (error instanceof ApiServiceError) {
        throw error;
      }
      throw new ApiServiceError(
        error instanceof Error ? error.message : 'Unknown error during query submission',
        undefined,
        'SUBMISSION_ERROR'
      );
    }
  }
}

// Export default instance
export const queryService = new QueryService();

/**
 * Convenience function to submit a query
 * 
 * @param queryData - Query submission data
 * @returns Promise resolving to SubmissionResponse
 */
export async function submitQuery(queryData: QuerySubmissionData): Promise<SubmissionResponse> {
  return queryService.submitQuery(queryData);
}